import React, { useState, useEffect } from 'react';
import IndexSelect from './IndexSelect';
import VariableInput from './VariableInput';
import SmartBadge from '../../../../components/SmartBadge/SmartBadge';

/**
 * props:
 * - indexes
 * - symbol
 * - conditions
 * - onChange
 * - variant: 'inline' | 'stacked'
 */
function ConditionsArea(props) {
    const variant = props.variant || 'inline';

    const [conditions, setConditions] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState({
        example: 0
    });
    const indexes = Array.isArray(props.indexes) ? props.indexes : [];

    function btnRemoveCondition(event) {
        const id = event.target.id;
        const pos = conditions.findIndex(c => c.eval === id);
        conditions.splice(pos, 1);
        if (props.onChange) props.onChange({ target: { id: 'conditions', value: conditions.map(c => c.eval).join(' && ') } });

        const conditionIndexes = parseIndexes(conditions);
        props.onChange({ target: { id: 'indexes', value: conditionIndexes } });
    }

    function onKeySelectChange(event) {
        const item = indexes.find(k => k.eval === event.target.value);
        if (item) setSelectedIndex(item);
    }

    function parseIndexes(conditionsArr) {
        const indexesStr = conditionsArr.map(condition => {
            const memoryIndexes = condition.eval.split(/[=><!]/)
                .filter(str => str.startsWith('MEMORY'))
                .map(str => str.split('.')[0]);

            return memoryIndexes.join(',');
        })
            .join(',');
        return [
            ...new Set(indexesStr.replaceAll("MEMORY['", "")
                .replaceAll("']", "")
                .replaceAll(".current", "")
                .split(','))
        ].join(',');
    }

    function onAddConditionClick(event) {
        const condition = event.target.value;
        if (!condition?.eval || conditions.some((c) => c.eval === condition.eval)) return;

        conditions.push({
            eval: condition.eval,
            text: condition.text || condition.eval,
        });
        props.onChange({ target: { id: 'conditions', value: conditions.map(c => c.eval).join(' && ') } });

        const conditionIndexes = parseIndexes(conditions);
        props.onChange({ target: { id: 'indexes', value: conditionIndexes } });
    }

    function formatConditionText(conditionEval) {
        const item = conditionEval.trim();
        const operatorMatch = item.match(/(>=|<=|==|!=|>|<)/);
        const operator = operatorMatch ? operatorMatch[1] : '==';

        const memoryMatch = item.match(/MEMORY\['([^']+)'\](?:\.current)?/);
        const memoryKey = memoryMatch ? memoryMatch[1] : item;

        const valuePart = item.slice((operatorMatch?.index || 0) + operator.length).trim();
        const displayOperator = operator === '==' ? '=' : operator;

        return `${memoryKey} ${displayOperator} ${valuePart}`
            .replaceAll(".current", "")
            .replaceAll(`${props.symbol}:`, "")
            .trim();
    }

    function parseConditions(conditionsText) {
        if (!conditionsText) return [];

        return conditionsText.split('&&').map((item) => {
            const evalText = item.trim();
            return {
                eval: evalText,
                text: formatConditionText(evalText),
            };
        });
    }

    useEffect(() => {
        const parsedConditions = parseConditions(props.conditions);
        setConditions(parsedConditions);
    }, [props.conditions])

    useEffect(() => {
        setSelectedIndex({ example: 0 });
    }, [props.symbol, props.market])

    const inputs = (
        <>
            <IndexSelect
                indexes={indexes}
                onChange={onKeySelectChange}
                variant={variant}
                placeholder={
                    !props.symbol
                        ? 'Select a trading pair first'
                        : (indexes.length === 0
                            ? `No ${String(props.market || 'spot').toLowerCase()} indicators for this pair`
                            : 'Select an indicator...')
                }
            />
            <VariableInput
                indexes={indexes}
                onAddClick={onAddConditionClick}
                selectedIndex={selectedIndex}
                variant={variant}
            />
        </>
    );

    return (
        <React.Fragment>
            {variant === 'stacked' ? (
                <div className="conditions-area--stacked">{inputs}</div>
            ) : (
                <div className="row">
                    <div className="col-12 mb-3">
                        {inputs}
                    </div>
                </div>
            )}
            {
                conditions
                    ? <div className="divScrollBadges">
                        <div className="d-inline-flex flex-row flex-wrap align-content-start">
                            {
                                conditions.map((condition, ix) => (
                                    <SmartBadge key={ix} id={condition.eval} text={condition.text} onClick={btnRemoveCondition} />
                                ))
                            }
                        </div>
                    </div>
                    : <React.Fragment></React.Fragment>
            }
        </React.Fragment>
    )
}

export default ConditionsArea;
